import React, { useMemo } from 'react';
import { FileUploadConfigurationProps } from 'js/components/data_sources/add_ds_form/FileUploadConfiguration/FileUploadConfiguration.types';
import { useGetPaging } from 'js/hooks/useGetPaging';
import PopUpForm from 'js/components/elements/pop-up-form/PopUpForm';
import RawFileDataCheckbox from 'js/components/data_sources/add_ds_form/RawFileDataCheckbox';
import FileDataFormatSection from 'new/components/flows/types/DirectFlow/steps/FileDataFormatSection/FileDataFormatSection';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';

import './pop-up-form-restyle.scss';

/**
 * Fields gated behind the processor-with-parser flag.
 * When the flag is OFF and the parser is active (not "Raw File Data"),
 * these fields and their dependents are removed entirely from the form.
 */
const PROCESSOR_FIELD_NAMES = new Set([
  'ui.use_custom_processor',
  'ai.function',
  // Dependents of the above — must also be removed to avoid orphaned fields
  'ui.custom_processor_mode',
  'ai.function.id',
]);

function removeProcessorFields(fields: any[]): any[] {
  return fields.filter((field) => !PROCESSOR_FIELD_NAMES.has(field.name));
}

export function FileUploadAdvancedConfigurationSection(props: FileUploadConfigurationProps) {
  const {
    sourceConfig,
    selectedConnection,
    disableConfiguration = false,
    buildSourceInfo,
    getCustomProcessorId,
    uploadedFiles,
  } = props;

  const { sourceConfigById } = useGetPaging();
  const processorWithParserEnabled = useFeatureFlag('UI_FILE_UPLOAD_PROCESSOR_WITH_PARSER_ENABLED');

  if (disableConfiguration) {
    return null;
  }
  const rawInputs = sourceConfigById[selectedConnection];
  const isSkipParsing = sourceConfig?.advanced_settings === 'Raw File Data';
  const inputs = useMemo(() => {
    if (processorWithParserEnabled || isSkipParsing) return rawInputs;
    return removeProcessorFields(rawInputs ?? []);
  }, [rawInputs, processorWithParserEnabled, isSkipParsing]);
  const onFieldUpdated = (_: string, __: string, newConfig: any) =>
    props.onSourceConfigChanged(newConfig);

  return (
    <div className="pop-up-form-restyle">
      <PopUpForm
        isModal={false}
        data={sourceConfig}
        showCloseButton={false}
        removeSectionHeading
        fields={inputs}
        isCredentialsForm={false}
        showRequiredIndicator={false}
        onFieldUpdated={onFieldUpdated}
        onSourceConfigChanged={props.onSourceConfigChanged}
        fieldClassNames="label-field"
        formPrepend={
          <RawFileDataCheckbox
            section="Define Processing Logic"
            sourceConfig={sourceConfig}
            sourceConfigFields={inputs ?? []}
            onSourceConfigChanged={props.onSourceConfigChanged}
            marginLeft={0}
            checkboxSx={{
              '& .MuiFormControlLabel-label': { fontWeight: 500 },
            }}
          />
        }
        renderSectionFields={(section, fields) => (
          <FileDataFormatSection
            section={section}
            fields={fields}
            sourceConfig={sourceConfig}
            buildSourceInfo={buildSourceInfo ?? (() => ({ source_config: sourceConfig }))}
            onSourceConfigChanged={props.onSourceConfigChanged}
            getProbeSampleCallback={null}
            useFlexFlowCustomSourceConfig
            getCustomProcessorId={getCustomProcessorId ?? (() => Promise.resolve(null))}
            uploadedFiles={uploadedFiles}
          />
        )}
      />
    </div>
  );
}
